import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';

/**
 * Onboarding tracker — the operator's "who's mid-onboarding" rail.
 *
 * Each row is one organization's journey, computed point-in-time by the backend
 * (GET /platform/onboarding-journeys): a progress bar, the single recommended
 * NEXT step (one-click), and a live count of events still carrying readiness
 * gaps. This is the forward-looking, journey-scoped sibling of the attention
 * strip — it answers "what should I do next to get this customer live?"
 */

interface EventGap {
  eventId: string;
  eventName: string;
  percent: number;
  gaps: { key: string; label: string; hint: string; path: string }[];
}
interface Journey {
  tenantId: string;
  tenantSlug?: string;
  tenantName: string;
  percent: number;
  completed: number;
  total: number;
  nextStep: { label: string; hint: string; action: { label: string; path: string } | null } | null;
  events: EventGap[];
  eventsReady: number;
  eventsWithGaps: number;
}

export function OnboardingTracker() {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient<Journey[]>('/platform/onboarding-journeys')
      .then((j) => { if (!cancelled) setJourneys(j || []); })
      .catch(() => { if (!cancelled) setJourneys([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <section style={styles.card}><div style={styles.muted}>Checking onboarding…</div></section>;
  if (journeys.length === 0) {
    return (
      <section style={styles.card}>
        <div style={styles.head}><h2 style={styles.h2}>🚀 Onboarding tracker</h2></div>
        <div style={styles.allGood}>✓ Every organization is fully onboarded.</div>
      </section>
    );
  }

  return (
    <section style={styles.card}>
      <div style={styles.head}>
        <h2 style={styles.h2}>🚀 Onboarding tracker</h2>
        <span style={styles.count}>{journeys.length} in progress</span>
      </div>
      <div style={styles.list}>
        {journeys.map((j) => {
          const isOpen = expanded === j.tenantId;
          return (
            <div key={j.tenantId} style={styles.row}>
              <div style={styles.rowMain}>
                <button style={styles.nameBtn} onClick={() => navigate(`/platform/organizations/${j.tenantSlug || j.tenantId}`)}>
                  <span style={styles.name}>{j.tenantName}</span>
                  <span style={styles.sub}>{j.completed}/{j.total} steps · {j.eventsReady} event(s) ready{j.eventsWithGaps > 0 ? ` · ${j.eventsWithGaps} with gaps` : ''}</span>
                </button>
                <div style={styles.barWrap}>
                  <div style={styles.track}><div style={{ ...styles.fill, width: `${j.percent}%`, background: j.percent >= 80 ? '#10b981' : j.percent >= 40 ? '#6366f1' : '#f59e0b' }} /></div>
                  <span style={styles.pct}>{j.percent}%</span>
                </div>
                {j.nextStep && (
                  <button
                    style={styles.nextBtn}
                    onClick={() => j.nextStep?.action && navigate(j.nextStep.action.path)}
                    title={j.nextStep.hint}
                  >
                    Next: {j.nextStep.label} →
                  </button>
                )}
                {j.events.length > 0 && (
                  <button style={styles.expandBtn} onClick={() => setExpanded(isOpen ? null : j.tenantId)} aria-label="Toggle event gaps">
                    {isOpen ? '▾' : '▸'} events
                  </button>
                )}
              </div>

              {isOpen && j.events.length > 0 && (
                <div style={styles.eventGaps}>
                  {j.events.map((e) => (
                    <div key={e.eventId} style={styles.eventRow}>
                      <span style={styles.eventName}>{e.eventName}</span>
                      <span style={styles.eventPct}>{e.percent}%</span>
                      <span style={styles.gapChips}>
                        {e.gaps.length === 0 ? (
                          <span style={styles.ready}>Ready ✓</span>
                        ) : (
                          e.gaps.slice(0, 4).map((g) => (
                            <button key={g.key} style={styles.gapChip} onClick={() => navigate(g.path)} title={g.hint}>
                              {g.label}
                            </button>
                          ))
                        )}
                        {e.gaps.length > 4 && <span style={styles.moreGaps}>+{e.gaps.length - 4}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const ACCENT = '#4f46e5';
const styles: Record<string, React.CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e8eaf0', borderRadius: 16, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)', marginBottom: 20 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  h2: { fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#0b1120' },
  count: { fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 },
  muted: { color: '#6b7280', padding: '10px 2px' },
  allGood: { color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '12px 14px', fontSize: '0.88rem', fontWeight: 600 },
  list: { display: 'flex', flexDirection: 'column', gap: 6 },
  row: { borderRadius: 12, border: '1px solid #f0f1f5', padding: '10px 12px' },
  rowMain: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  nameBtn: { background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0, display: 'flex', flexDirection: 'column', minWidth: 180, flex: 1 },
  name: { fontWeight: 800, color: '#0b1120', fontSize: '0.95rem' },
  sub: { fontSize: '0.76rem', color: '#6b7280', marginTop: 2 },
  barWrap: { display: 'flex', alignItems: 'center', gap: 8, width: 150 },
  track: { flex: 1, height: 8, background: '#eef0f4', borderRadius: 999, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 999 },
  pct: { fontSize: '0.76rem', color: '#475569', width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  nextBtn: { background: '#eef2ff', color: ACCENT, border: 'none', borderRadius: 999, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  expandBtn: { background: 'transparent', border: 'none', color: '#6b7280', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  eventGaps: { marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e8eaf0', display: 'flex', flexDirection: 'column', gap: 8 },
  eventRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  eventName: { fontSize: '0.84rem', fontWeight: 700, color: '#334155', minWidth: 140 },
  eventPct: { fontSize: '0.74rem', color: '#6b7280', width: 36, fontVariantNumeric: 'tabular-nums' },
  gapChips: { display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 },
  gapChip: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 999, padding: '3px 10px', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' },
  moreGaps: { fontSize: '0.74rem', color: '#9aa4b2', alignSelf: 'center' },
  ready: { fontSize: '0.76rem', color: '#10b981', fontWeight: 700 },
};
